import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  ViewChild
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { AddressSummaryService } from "@onsip/common/services/api/resources/addressSummary/address-summary.service";
import { NonNullableFormBuilder, Validators } from "@angular/forms";
import { ExtensionsListComponent } from "@onsip/web/features/shared/components/extensionsList/extensions-list.component";
import phoneNumberValidator from "@onsip/common/libraries/validators/phone-number-validators";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { TelephoneNumberAddressService } from "@onsip/common/services/api/resources/telephoneNumberAddress/telephone-number-address.service";
import { views } from "@onsip/web/app/phone/views";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { BehaviorSubject, Subscription } from "rxjs";
import { map, take } from "rxjs/operators";
import { User } from "@onsip/common/services/api/resources/user/user";
import { TelephoneNumberAddressIdentityService } from "@onsip/common/services/api/resources/telephoneNumberAddress/telephone-number-address-identity.service";
import { TelephoneNumberAddressParams } from "@onsip/common/services/api/resources/telephoneNumberAddress/telephone-number-address";
import { E2eLocators } from "@onsip/e2e/configs/locators";
import { NAME_VALIDATOR_REGEX } from "@onsip/common/libraries/validators/name-validators";

@Component({
  selector: "onsip-external-phone-number-setup",
  templateUrl: "./external-phone-number-setup.component.html",
  styleUrls: ["./external-phone-number-setup.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class ExternalPhoneNumberSetupComponent implements OnInit, OnDestroy {
  @ViewChild("extensionsList") extensionsListRef!: ExtensionsListComponent;
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  allUsers: Array<User> = [];
  domain = "";
  organizationId = "";
  externalPhoneNumberForm = this.fb.group({
    name: [
      "",
      [Validators.required, Validators.maxLength(60), Validators.pattern(NAME_VALIDATOR_REGEX)]
    ],
    externalPhoneNumber: ["", [Validators.required, phoneNumberValidator()]],
    callingType: [""],
    reportCallActivity: [<string | undefined>undefined],
    callerIdType: [""],
    customCallerIdName: [""],
    customCallerIdNumber: [""],
    extensions: [<Array<string>>[]]
  });

  E2eLocators = E2eLocators;
  loadingStateComplete$ = new BehaviorSubject(true);

  protected unsubscriber = new Subscription();

  constructor(
    protected addressSummary: AddressSummaryService,
    protected translate: TranslateService,
    protected numberAddressService: TelephoneNumberAddressService,
    protected addressIdentityService: TelephoneNumberAddressIdentityService,
    protected router: Router,
    protected user: UserService,
    protected snackbar: SnackbarService,
    protected dialog: MatDialog,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit(): void {
    this.initFields();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  initFields(): void {
    this.getUsers();
    this.setCallerIdValidators();
    this.externalPhoneNumberForm.controls.callingType.setValue("unrestricted");
    this.externalPhoneNumberForm.controls.callerIdType.setValue("default");
  }

  getUsers(): void {
    this.unsubscriber.add(
      this.user.orgState
        .pipe(
          map(allUsers => {
            this.externalPhoneNumberForm.controls.reportCallActivity.setValue(allUsers[0].userId);
            this.domain = allUsers[0].domain;
            this.organizationId = allUsers[0].organizationId;
            return allUsers;
          })
        )
        .subscribe(allUsers => {
          this.allUsers = allUsers;
        })
    );
  }

  setCallerIdValidators(): void {
    this.unsubscriber.add(
      this.externalPhoneNumberForm.controls.callerIdType.valueChanges.subscribe(value => {
        if (value === "custom") {
          this.externalPhoneNumberForm.controls.customCallerIdName.setValidators(
            Validators.required
          );
        } else {
          this.externalPhoneNumberForm.controls.customCallerIdName.clearValidators();
        }
      })
    );
  }

  getNameError(): string {
    if (this.externalPhoneNumberForm.controls.name.hasError("maxlength")) {
      return this.translate.instant("ONSIP_I18N.MUST_BE_LESS_THAN_60_CHARACTERS");
    }
    if (this.externalPhoneNumberForm.controls.name.hasError("pattern")) {
      return this.translate.instant(
        "ONSIP_I18N.MUST_CONTAIN_ONLY_ALPHANUMERIC_CHARACTERS_SPACES_AND_NO_PLUS_COMMA_COLON"
      );
    }
    if (this.externalPhoneNumberForm.controls.name.hasError("required")) {
      return this.translate.instant("ONSIP_I18N.NAME_IS_REQUIRED");
    }
    return "";
  }

  formatPhoneNumber(): void {
    let formattedNumber = "";
    for (
      let i = 0;
      i < this.externalPhoneNumberForm.controls.externalPhoneNumber.value.length;
      i++
    ) {
      if (!isNaN(parseInt(this.externalPhoneNumberForm.controls.externalPhoneNumber.value[i]))) {
        formattedNumber += this.externalPhoneNumberForm.controls.externalPhoneNumber.value[i];
      }
    }
    if (formattedNumber.length === 10) {
      this.externalPhoneNumberForm.controls.externalPhoneNumber.setValue("1" + formattedNumber);
      return;
    }
    if (formattedNumber.length === 11) {
      this.externalPhoneNumberForm.controls.externalPhoneNumber.setValue(formattedNumber);
    }
    return;
  }

  onCancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj?.doPrimaryAction) {
            this.router.navigate([views.ADMIN_EXTERNAL_PHONE_NUMBER]);
          }
        })
    );
  }

  showSaveSnackbar(): void {
    this.snackbar.openSnackBar(
      this.translate.instant("ONSIP_I18N.EXTERNAL_PHONE_NUMBER_SUCCESSFULLY_ADDED"),
      "success"
    );
  }

  showErrorSnackbar(message: string): void {
    this.snackbar.openSnackBar(message, "error");
    this.numberAddressService.clearErrors();
  }

  execCallerIdAdd(): void {
    this.addressIdentityService
      .telephoneNumberAddressIdentityAdd({
        Address:
          this.externalPhoneNumberForm.controls.externalPhoneNumber.value + "@" + this.domain,
        CallerIdName: this.externalPhoneNumberForm.controls.customCallerIdName.value,
        CallerIdNumber: this.externalPhoneNumberForm.controls.customCallerIdNumber.value
      })
      .then(result => {
        if (result.status === "success") {
          this.showSaveSnackbar();
          this.router.navigate([views.ADMIN_EXTERNAL_PHONE_NUMBER]);
        } else {
          this.showErrorSnackbar(result.data.message);
          this.addressIdentityService.clearErrors();
        }
      });
  }

  onSubmit(): void {
    if (this.externalPhoneNumberForm.invalid) {
      return;
    }
    this.loadingStateComplete$.next(false);
    this.formatPhoneNumber();
    const params: TelephoneNumberAddressParams = {
      OrganizationId: this.organizationId,
      Domain: this.domain,
      Name: this.externalPhoneNumberForm.controls.name.value,
      UserId:
        (this.externalPhoneNumberForm.controls.callingType.value === "unrestricted" &&
          this.externalPhoneNumberForm.controls.reportCallActivity.value) ||
        "",
      Username: this.externalPhoneNumberForm.controls.externalPhoneNumber.value
    };
    this.numberAddressService.telephoneNumberAddressAdd(params).then(result => {
      if (result.status === "success") {
        if (this.externalPhoneNumberForm.controls.callerIdType.value === "custom") {
          this.extensionsListRef.editExtensions(
            this.externalPhoneNumberForm.controls.externalPhoneNumber.value
          );
          this.execCallerIdAdd();
        } else {
          this.extensionsListRef.editExtensions(
            this.externalPhoneNumberForm.controls.externalPhoneNumber.value
          );
          this.showSaveSnackbar();
          this.router.navigate([views.ADMIN_EXTERNAL_PHONE_NUMBER]);
        }
      } else {
        this.showErrorSnackbar(result.data.message);
        this.numberAddressService.clearErrors();
      }
      this.loadingStateComplete$.next(true);
    });
  }
}
